import React, { useCallback, useEffect, useRef, useState } from "react";
import { searchExistingEvidence, listEvidenceAssociations, linkExistingEvidence, unlinkEvidence, downloadEvidenceUrl } from "../api/evidence.api";
import { useAuth } from "../useAuth";
import { hasModuleAccess } from "../accessControl";
import Button from "../ui/Button";
import Input from "../ui/Input";
import ConfirmDialog from "./common/ConfirmDialog";
import DestructiveConfirm from "./common/DestructiveConfirm";
import EvidenceDocumentViewer from "./EvidenceDocumentViewer";

const label = (evidence) => evidence.original_filename || evidence.url || `Evidence ${evidence.id}`;
const payload = (response) => response?.data ?? response;

export default function EvidenceAssociations({ parentType, parentId, writable = true }) {
  const { user } = useAuth() || {};
  const canEdit = writable && hasModuleAccess(user, { risk: "risks", control: "controls", action: "actions" }[parentType], "EDIT");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [remove, setRemove] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);
  const listRequest = useRef(0);
  const invalidateList = useCallback(() => { ++listRequest.current; }, []);
  const load = useCallback(async () => {
    const version = ++listRequest.current;
    try {
      const data = payload(await listEvidenceAssociations(parentType, parentId)) || [];
      if (version === listRequest.current) setItems(data);
    }
    catch { if (version === listRequest.current) setError("Linked evidence is unavailable. Refresh to try again."); }
  }, [parentType, parentId]);
  useEffect(() => {
    setItems([]); setError(""); load();
    return invalidateList;
  }, [load, invalidateList]);
  useEffect(() => {
    const version = ++request.current;
    if (!open) return;
    setResults([]); setCursor(null); setSelected(null);
    const timer = setTimeout(async () => {
      setBusy(true); setError("");
      try {
        const data = payload(await searchExistingEvidence({ search }));
        if (version === request.current) { setResults(data.items); setCursor(data.next_cursor); }
      } catch { if (version === request.current) setError("Evidence search failed. Try again."); }
      finally { if (version === request.current) setBusy(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, search]);
  const confirmLink = async () => {
    setBusy(true); setError("");
    try { await linkExistingEvidence(parentType, parentId, selected.id); setOpen(false); setSelected(null); await load(); }
    catch { setError("Unable to link evidence. Check your access and try again."); }
    finally { setBusy(false); }
  };
  return <section aria-label="Linked evidence" style={{ display: "grid", gap: 12, marginTop: 16 }}>
    <h3>Linked evidence</h3>
    {canEdit && <Button onClick={() => { setSearch(""); setOpen(true); }}>Link existing evidence</Button>}
    {error && <p role="alert">{error}</p>}
    {items.length === 0 && <p>No linked evidence.</p>}
    {items.map(({ association_id, evidence }) => <div key={association_id} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <strong>{label(evidence)}</strong>
      {evidence.type === "file" ? <>
        <Button variant="ghost" onClick={() => setViewer(evidence)}>View</Button>
        <Button variant="secondary" onClick={() => window.open(downloadEvidenceUrl(evidence.id), "_blank", "noopener,noreferrer")}>Download</Button>
      </> : <Button variant="secondary" onClick={() => window.open(evidence.url, "_blank", "noopener,noreferrer")}>Open link</Button>}
      {canEdit && <Button variant="destructive" onClick={() => setRemove(evidence)}>Remove link</Button>}
    </div>)}
    <ConfirmDialog open={open} title="Link existing evidence" confirmLabel="Link evidence" confirmVariant="primary"
      confirmDisabled={!selected || busy} onCancel={() => { if (!busy) setOpen(false); }} onConfirm={confirmLink}
      message={<div>
        <Input label="Search documents" value={search} onChange={(event) => setSearch(event.target.value)} />
        <p>Select a document. Its original permissions continue to apply.</p>
        {busy && <p role="status">Loading…</p>}
        {error && <p role="alert">{error}</p>}
        {!busy && results.length === 0 && <p>No authorised evidence found.</p>}
        {results.map((evidence) => <label key={evidence.id} style={{ display: "block", padding: 8 }}>
          <input type="radio" name="existing-evidence" checked={selected?.id === evidence.id} disabled={busy} onChange={() => setSelected(evidence)} /> {label(evidence)}
        </label>)}
        {cursor && <Button disabled={busy} variant="secondary" onClick={async () => {
          const version = request.current;
          setBusy(true);
          try { const data = payload(await searchExistingEvidence({ search, cursor })); if (version === request.current) { setResults((previous) => [...previous, ...data.items]); setCursor(data.next_cursor); } }
          catch { setError("Evidence search failed. Try again."); }
          finally { if (version === request.current) setBusy(false); }
        }}>Load more</Button>}
      </div>} />
    <DestructiveConfirm actionId="EVIDENCE_UNLINK" open={Boolean(remove)} resourceDisplayName={remove ? label(remove) : ""}
      onCancel={() => setRemove(null)} onConfirm={async () => {
        try { await unlinkEvidence(parentType, parentId, remove.id); setRemove(null); await load(); }
        catch { setRemove(null); setError("Unable to remove link. Check your access and try again."); }
      }} />
    <EvidenceDocumentViewer open={Boolean(viewer)} onClose={() => setViewer(null)} title={viewer ? label(viewer) : "Evidence"}
      filename={viewer?.original_filename} mimeType={viewer?.mime_type} sizeBytes={viewer?.size_bytes} downloadUrl={viewer ? downloadEvidenceUrl(viewer.id) : null} />
  </section>;
}
